import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, Zap, Loader2 } from 'lucide-react';

const CreditActivity = () => {
  const { api, user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const res = await api.get('/credits/activity');
      setActivities(res.data);
    } catch (err) {
      console.error('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="credit-activity">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          <span className="gradient-text">Credit Activity</span>
        </h1>
        <p className="text-slate-400 mt-1">Track your credit usage across all features</p>
      </div>

      {/* Current Credits Summary */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Current Balance</p>
              <span className="text-4xl font-bold gradient-text" data-testid="credit-balance">
                {(user?.credits || 0) + (user?.purchasedCredits || 0)}
              </span>
              <span className="text-slate-500 ml-2">credits</span>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/20">
              <Zap className="w-8 h-8 text-violet-400" />
            </div>
          </div>
          <div className="flex gap-6 mt-4 text-sm">
            <span className="text-slate-400">
              Monthly: <span className="text-white font-medium">{user?.credits || 0}</span>
            </span>
            <span className="text-slate-400">
              Purchased: <span className="text-cyan-400 font-medium">{user?.purchasedCredits || 0}</span>
            </span>
            <span className="text-slate-400">
              Plan: <span className="text-violet-400 font-medium capitalize">{user?.subscriptionPlan}</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" />
            Activity History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activities.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <p>No credit activity yet</p>
              <p className="text-sm mt-1">Your credit usage will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {activities.map((activity) => (
                <div 
                  key={activity.id} 
                  className="flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors"
                  data-testid="activity-item"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{activity.action}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                    -{activity.creditsUsed} credits
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Note */}
      <Card className="bg-slate-800/30 border-slate-800">
        <CardContent className="p-4 text-sm text-slate-400">
          <p>
            <strong className="text-slate-300">Note:</strong> Writing Tools (Hooks, Captions, Hashtags) and the 30-Day Planner are free and don't appear in this activity log.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditActivity;
